import { Close as CloseIcon } from '@mui/icons-material';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Paper, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDataProvider, useRecordContext } from 'react-admin';
import { setupEcho } from '../../providers/sockets/laravel-echo';

interface TerminalFeedbackProps {
  /** Whether the terminal dialog is open */
  open: boolean;

  /** Function to call when closing the dialog */
  onClose: () => void;

  /** Unit hostname for WebSocket connection */
  hostname: string;

  /** Command execution UUID */
  executionUuid: string;

  /** Command execution ID for API calls */
  executionId: number;

  /** Command name for display */
  commandName: string;

  /** Machine-readable command slug (Command.name), used as the third segment of the CMD_ACK topic */
  commandSlug: string;

  /** Feedback configuration */
  feedbackConfig?: {
    wait_type: 'done_message' | 'topic_message';
    custom_topic?: string;
    custom_message?: string;
    show_terminal: boolean;
  };
}

/**
 * Terminal Feedback Dialog
 *
 * Displays real-time command output via WebSocket and monitors
 * for completion messages based on feedback configuration.
 */
export const TerminalFeedback: React.FC<TerminalFeedbackProps> = ({
  open,
  onClose,
  hostname,
  executionUuid,
  executionId,
  commandName,
  commandSlug,
  feedbackConfig,
}) => {
  const [output, setOutput] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<any>(null);
  const record = useRecordContext();
  const dataProvider = useDataProvider();

  // Auto-scroll to bottom when new output arrives
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  // Laravel Echo connection for real-time output
  useEffect(() => {
    if (!open || !executionUuid || !record?.id) return;

    // Prevent duplicate connections
    if (channelRef.current) {
      console.log('Connection already exists, skipping setup');
      return;
    }

    // Get auth token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found');
      setOutput(['[Error] Authentication token not found']);
      return;
    }

    // Get waiting message directly to avoid dependency issues
    const getWaitingMessage = (): string => {
      if (!feedbackConfig) {
        return 'Waiting for command completion...';
      }

      if (feedbackConfig.wait_type === 'done_message') {
        return `Waiting for done message on CMD_ACK/${hostname}/${commandSlug}`;
      } else if (feedbackConfig.wait_type === 'topic_message') {
        const topic = feedbackConfig.custom_topic?.replace('{hostname}', hostname) || 'custom topic';
        return `Waiting for message on ${topic}`;
      }

      return 'Waiting for command completion...';
    };

    // Fetch historical ACK messages first
    const loadHistoricalOutput = async (): Promise<{ output: string[]; isCompleted: boolean }> => {
      try {
        const ackResponse = await dataProvider.getCommandExecutionAckMessage(record.id, executionId);
        console.log('Raw ACK response:', ackResponse);
        const historicalOutput: string[] = [];
        let isCompleted = false;

        if (ackResponse?.data?.feedback_output) {
          // Split the feedback_output by lines and add each as a historical entry
          const lines = ackResponse.data.feedback_output.split('\n').filter((line: string) => line.trim() !== '');
          lines.forEach((line: string) => {
            historicalOutput.push(`[History] ${line}`);
          });
        }

        // Check if command is already completed
        if (ackResponse?.data?.feedback_completed) {
          isCompleted = true;
          historicalOutput.push('[Complete] Command finished successfully');
        }

        return { output: historicalOutput, isCompleted };
      } catch (error) {
        console.log('No historical output available or error fetching:', error);
        return { output: [], isCompleted: false };
      }
    };

    // Setup Echo and subscribe to channel
    const initializeTerminal = async () => {
      try {
        // Reset state when starting initialization
        setIsComplete(false);
        setError(null);

        // Load historical output first
        const historicalData = await loadHistoricalOutput();
        console.log('Historical output loaded:', historicalData);

        // Set completion status if command was already completed
        if (historicalData.isCompleted) {
          setIsComplete(true);
        }

        // Set initial output with history and connection messages
        const initialOutput = [
          '[Connected] Monitoring command: ' + commandName,
          '[Waiting] ' + getWaitingMessage(),
          ...historicalData.output,
          ...(historicalData.output.length > 0 && !historicalData.isCompleted
            ? ['[Info] --- Historical output above, live output below ---']
            : []),
        ];

        console.log('Setting initial output:', initialOutput);
        setOutput(initialOutput);

        // Setup WebSocket connection
        await setupEcho(token);

        // Only set up channel if we don't already have a connection
        if (!channelRef.current) {
          // Subscribe to private channel for this command execution
          // @ts-ignore
          const channel = window.Echo.private(`command-feedback.${executionUuid}`);
          channelRef.current = channel;

          // Listen for command feedback events
          channel.listen('.command.feedback', (event: any) => {
            console.log('Feedback received:', event);

            if (event.type === 'output') {
              // Real-time command output
              setOutput((prev) => [...prev, event.message]);
            } else if (event.type === 'completion') {
              // Command completed
              setIsComplete(true);
              setOutput((prev) => [...prev, `[Complete] ${event.message || 'Command finished successfully'}`]);
            } else if (event.type === 'error') {
              // Error occurred
              setError(event.message);
              setOutput((prev) => [...prev, `[Error] ${event.message}`]);
            }
          });
        }
      } catch (err) {
        console.error('Failed to setup terminal:', err);
        setError('Connection error occurred');
        setOutput((prev) => [...prev, '[Error] Failed to connect to server']);
      }
    };

    initializeTerminal();

    // Cleanup on unmount
    return () => {
      console.log('Cleaning up Echo channel');
      if (channelRef.current) {
        channelRef.current.stopListening('.command.feedback');
        // @ts-ignore
        window.Echo?.leave(`command-feedback.${executionUuid}`);
        channelRef.current = null;
      }
    };
  }, [open, executionUuid, executionId, commandName, commandSlug, hostname, feedbackConfig, record?.id, dataProvider]);

  const handleClose = () => {
    // Unsubscribe from Echo channel
    if (channelRef.current) {
      channelRef.current.stopListening('.command.feedback');
      // @ts-ignore
      window.Echo?.leave(`command-feedback.${executionUuid}`);
      channelRef.current = null;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Tovertafel: {record?.serial} / Command Feedback: {commandName}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Paper
          ref={outputRef}
          sx={{
            'backgroundColor': '#1e1e1e',
            'color': '#d4d4d4',
            'fontFamily': 'monospace',
            'fontSize': '14px',
            'padding': 2,
            'flex': 1,
            'overflow': 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#2d2d2d',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#555',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#777',
            },
          }}
        >
          {output.length === 0 ? (
            <Typography sx={{ color: '#888' }}>Connecting...</Typography>
          ) : (
            <>
              {console.log('Rendering output:', output)}
              {output.map((line, index) => (
                <Box
                  key={index}
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    mb: 0.5,
                    color: line.startsWith('[Error]')
                      ? '#f48771'
                      : line.startsWith('[Complete]')
                        ? '#4ec9b0'
                        : line.startsWith('[Connected]') || line.startsWith('[Waiting]')
                          ? '#569cd6'
                          : line.startsWith('[History]')
                            ? '#dcdcaa'
                            : line.startsWith('[Info]')
                              ? '#9cdcfe'
                              : '#d4d4d4',
                  }}
                >
                  {line}
                </Box>
              ))}
            </>
          )}
        </Paper>

        {isComplete && (
          <Box mt={2} p={2} bgcolor="success.light" borderRadius={1}>
            <Typography variant="body2" color="success.dark">
              ✓ Command completed successfully
            </Typography>
          </Box>
        )}

        {error && (
          <Box mt={2} p={2} bgcolor="error.light" borderRadius={1}>
            <Typography variant="body2" color="error.dark">
              ✗ {error}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
